/**
 * "No one has reported feeling sick": a gentle note for the owner when a long run of shifts shows no health answer other than
 * "all clear". Ontology pass 2026-10-04, move 4: today that run reads as success, but it can also mean people are not saying so.
 *
 * What it is, and is not:
 *  - The owner only. Never staff, never a shift lead, never a manager, never an admin.
 *  - A question about the team's habit, not a doubt about any person. It carries two counts and no names, no answers.
 *  - It never says anyone lied, and it asks for one thing: remind the team that saying so is safe (G2b: the employee's dignity).
 *
 * The threshold is a practice, not a statistic. We found no source that gives a rate to compare against, so none is claimed.
 * The numbers below are conservative on purpose: a small or part-time team never reaches them, so the note appears only where
 * a quiet record is long enough to be worth a second look. Tune them with real use.
 */
import type { DailyCheckin, IsoDate } from "./model.ts";
import { checkinAction } from "./checkin.ts";

/** How far back we look (days, today included). The note speaks of the days that actually had check-ins, not this window. */
export const ZERO_REPORT_WINDOW_DAYS = 60;
/** At least this many check-ins in the window (about 4 people on 50 days)... */
export const ZERO_REPORT_MIN_CHECKINS = 200;
/** ...on at least this many different days, so one busy stretch cannot trigger it. */
export const ZERO_REPORT_MIN_DAYS = 45;
/** "Not now" hides the note this long. */
export const ZERO_REPORT_SNOOZE_DAYS = 14;

/** Only counts: how many check-ins and on how many days. No names, no answers. */
export interface ZeroReportNote { checkins: number; days: number }

type C = Pick<DailyCheckin, "date" | "health">;

const shift = (d: IsoDate, n: number): IsoDate => new Date(Date.parse(`${d}T12:00:00Z`) + n * 864e5).toISOString().slice(0, 10);

/**
 * The note, or null. A "report" is any check-in answer that changes what the person does today (checkinAction is not "ok":
 * a symptom, a diagnosis, a sore, someone sick at home). Any one of them in the window means the team does say so: no note.
 */
export function zeroReportNote(checkins: readonly C[], today: IsoDate): ZeroReportNote | null {
  const from = shift(today, -(ZERO_REPORT_WINDOW_DAYS - 1));
  const inWindow = checkins.filter((c) => c.date >= from && c.date <= today);
  if (inWindow.length < ZERO_REPORT_MIN_CHECKINS) return null;
  if (inWindow.some((c) => checkinAction(c.health) !== "ok")) return null;
  const days = new Set(inWindow.map((c) => c.date)).size;
  return days >= ZERO_REPORT_MIN_DAYS ? { checkins: inWindow.length, days } : null;
}

/** The owner: the one role that sees the note. Everyone else, never. */
export const seesZeroReportNote = (role: string | undefined): boolean => role === "owner";

/** The words (COPY-STANDARD: short, active, the reader leads, no blame). One place, so web, phone and API say the same. */
export const ZERO_REPORT_WORDS = {
  kicker: "Worth a look",
  title: (days: number) => `No one has reported feeling sick in ${days} days of shifts.`,
  body: "That's possible, but it's rarer than it looks. A short reminder tells the team it's safe to say so. If you pay for the shift either way, say that too.",
  remind: "Remind the team",
  notNow: "Not now",
  caption: "Only you see this note. It names no one.",
  sent: "Reminder sent to the team.",
  /** What each person reads on their own Today. Neutral: it shows nothing about anyone, and says only what is true of who sees a result. */
  message: "A reminder from your manager: if you feel sick, say so on your check-in. It's safe to say. Only the people who plan your shift see the result, and your coworkers never do.",
} as const;

/** "Not now" memory: the note stays hidden until this date (an ISO day), counted from the day it was tapped. */
export const zeroReportSnoozedUntil = (today: IsoDate): IsoDate => shift(today, ZERO_REPORT_SNOOZE_DAYS);
export const zeroReportSnoozed = (until: string | null | undefined, today: IsoDate): boolean => !!until && until > today;
